import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle, Shield, CreditCard, Banknote, Upload, Loader2, Copy, AlertCircle, Info } from 'lucide-react';
import { convertPrice, EXCHANGE_RATE } from '@utils/helpers';
import { PRICING_PLANS } from '@utils/constants';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const BANK_DETAILS = {
  bankName: 'GTBank (Guaranty Trust Bank)',
  accountName: 'NELSON EMEM FEYIKEMI',
  accountNumber: '0678121228',
  sortCode: '058',
};

const BookingModal = ({ isOpen, onClose, currency, activeTab, setActiveTab }) => {
  const { user, profile } = useAuth() || {};
  const [bookingStep, setBookingStep] = useState(1);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentType, setPaymentType] = useState('full');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedReceipt, setUploadedReceipt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [modalError, setModalError] = useState({ type: '', text: '' });

  const showModalError = (type, text, autoDismiss = true) => {
    setModalError({ type, text });
    if (autoDismiss) {
      setTimeout(() => {
        setModalError(prev => prev.text === text ? { type: '', text: '' } : prev);
      }, 7000);
    }
  };
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const selectedPlan = PRICING_PLANS.find(p => p.id === activeTab) || PRICING_PLANS[0];

  const actualPriceToPay = paymentType === 'installment' && selectedPlan.id === 'premium'
    ? selectedPlan.price * 0.7
    : selectedPlan.price;

  const flutterwaveAmount = currency === 'USD' 
    ? Math.round(actualPriceToPay * EXCHANGE_RATE) 
    : actualPriceToPay;

  const flutterwaveConfig = {
    public_key: import.meta.env.VITE_FLUTTERWAVE_PUBLIC_KEY || '',
    tx_ref: Date.now().toString(),
    amount: flutterwaveAmount,
    currency: currency,
    payment_options: 'card,mobilemoney,ussd',
    customer: {
      email: bookingData.email,
      phone_number: bookingData.phone,
      name: bookingData.name,
    },
    customizations: {
      title: 'Placement Point',
      description: `Payment for ${selectedPlan.name} Plan`,
      logo: 'https://st2.depositphotos.com/4403291/7418/v/450/depositphotos_74189661-stock-illustration-online-shop-log.jpg',
    },
  };

  const handleFlutterPayment = useFlutterwave(flutterwaveConfig);

  const validateForm = () => {
    const errors = {};
    if (!bookingData.name.trim()) errors.name = 'Full name is required';
    if (!bookingData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.email)) errors.email = 'Please enter a valid email';
    if (!bookingData.phone.trim()) errors.phone = 'Phone number is required';
    else if (bookingData.phone.replace(/\D/g, '').length < 7) errors.phone = 'Phone number must have at least 7 digits';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinueToPayment = () => {
    if (!validateForm()) return;
    if (!acceptedTerms) {
      setTermsError(true);
      return;
    }
    setTermsError(false);
    setFormErrors({});
    setBookingStep(2);
  };

  const handlePayment = async () => {
    if (paymentMethod === 'transfer' && !uploadedReceipt) {
      showModalError('error', 'Please upload your payment receipt before proceeding.');
      return;
    }
    
    if (paymentMethod === 'card') {
      if (!import.meta.env.VITE_FLUTTERWAVE_PUBLIC_KEY) {
        showModalError('error', 'Flutterwave public key is not configured.');
        return;
      }
      
      handleFlutterPayment({
        callback: async (response) => {
           console.log('Flutterwave response:', response);
           closePaymentModal();
           if (response.status === 'successful') {
              if (user) {
                try {
                  let baseDate = new Date();
                  if (profile?.subscription_expiry) {
                    const currentExpiry = new Date(profile.subscription_expiry);
                    if (currentExpiry > baseDate) {
                      baseDate = currentExpiry;
                    }
                  }
                  
                  const expiryDate = new Date(baseDate);
                  expiryDate.setMonth(expiryDate.getMonth() + 1);
                 
                 await supabase
                   .from('payments')
                   .insert({
                     user_id: user.id,
                     amount: flutterwaveAmount,
                     plan: selectedPlan.id,
                     type: 'flutterwave',
                     status: 'successful',
                     notes: `Card payment via Flutterwave for ${selectedPlan.name} Plan (${paymentType})`
                   });
                 
                 await supabase
                   .from('profiles')
                   .update({
                     subscription_status: 'active',
                     subscription_plan: selectedPlan.id,
                     phone: bookingData.phone,
                     subscription_payment_type: paymentType,
                     subscription_expiry: expiryDate.toISOString(),
                     subscription_renewal_date: expiryDate.toISOString(),
                     expiry_warning_sent: false,
                     expiry_email_sent: false
                   })
                   .eq('id', user.id);
               } catch (err) {
                 console.error('Error recording card payment or updating profile:', err);
               }
             }
             setBookingStep(3);
           } else {
             showModalError('error', 'Payment was not successful. Please try again.');
           }
        },
        onClose: () => {
          // Modal closed
        },
      });
    } else {
      if (!user) {
        showModalError('error', 'Please log in to complete your subscription.');
        return;
      }
      setIsLoading(true);
      try {
        // Upload receipt to Supabase Storage
        const file = uploadedReceipt;
        const fileName = `${user.id}/${Date.now()}_${file.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('documents')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Insert record into payments table
        const { error: payError } = await supabase
          .from('payments')
          .insert({
            user_id: user.id,
            amount: flutterwaveAmount,
            plan: selectedPlan.id,
            type: 'bank_transfer',
            status: 'pending',
            receipt_url: uploadData.path,
            notes: `Bank Transfer for ${selectedPlan.name} Plan (${paymentType})`
          });

        if (payError) throw payError;

        // Update profile with phone and subscription details (pending status)
        let baseDate = new Date();
        if (profile?.subscription_expiry) {
          const currentExpiry = new Date(profile.subscription_expiry);
          if (currentExpiry > baseDate) {
            baseDate = currentExpiry;
          }
        }
        
        const expiryDate = new Date(baseDate);
        expiryDate.setMonth(expiryDate.getMonth() + 1);
        
        await supabase
          .from('profiles')
          .update({
            phone: bookingData.phone,
            subscription_payment_type: paymentType,
            subscription_expiry: expiryDate.toISOString(),
            subscription_renewal_date: expiryDate.toISOString(),
            expiry_warning_sent: false,
            expiry_email_sent: false
          })
          .eq('id', user.id);

        showModalError('success', 'Transfer receipt uploaded successfully! Our team will verify it within 24 hours.', false);
        
        setTimeout(() => {
          setBookingStep(3);
        }, 1500);
      } catch (error) {
        console.error('Payment failed:', error);
        showModalError('error', `Payment submission failed: ${error.message || 'Please try again.'}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type.includes('word'))) {
      setUploadedFile(file);
    } else {
      showModalError('error', 'Please upload a PDF or Word document');
    }
  };

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
      setUploadedReceipt(file);
    } else {
      showModalError('error', 'Please upload an image (JPG, PNG) or PDF');
    }
  };

  const handleCompleteBooking = async () => {
    if (!user) {
      showModalError('error', 'Please log in to complete your booking.');
      return;
    }
    setIsLoading(true);
    try {
      if (uploadedFile) {
        const file = uploadedFile;
        const fileName = `${user.id}/${Date.now()}_${file.name}`;
        
        // 1. Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(fileName, file);
        if (uploadError) throw uploadError;

        // 2. Insert to documents table
        const { error: dbError } = await supabase
          .from('documents')
          .insert({
            user_id: user.id,
            file_name: file.name,
            file_url: fileName,
            file_type: file.type,
            file_size: file.size,
          });
        if (dbError) throw dbError;
      }

      showModalError('success', 'Booking completed successfully! We will verify your payment and review your CV within 24 hours. We look forward to working with you! 🚀', false);
      setTimeout(() => {
        resetBooking();
        setModalError({ type: '', text: '' });
        window.location.reload();
      }, 3500);
    } catch (error) {
      console.error('Booking failed:', error);
      showModalError('error', `Failed to complete booking: ${error.message || 'Please try again.'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const resetBooking = () => {
    setBookingStep(1);
    setAcceptedTerms(false);
    setTermsError(false);
    setUploadedFile(null);
    setUploadedReceipt(null);
    setPaymentType('full');
    setFormErrors({});
    setBookingData({ name: '', email: '', phone: '' });
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-2xl font-bold text-slate-900">Book Your Session</h3>
          <button onClick={resetBooking} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        <div className="p-6">
          {/* Modal Inline Notification Banner */}
          {modalError.text && (
            <div
              className={`mb-6 p-4 rounded-xl border flex items-start gap-3 transition-all duration-300 ${
                modalError.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : modalError.type === 'error'
                  ? 'bg-rose-50 border-rose-200 text-rose-800'
                  : 'bg-indigo-50 border-indigo-200 text-indigo-800'
              }`}
            >
              {modalError.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              ) : modalError.type === 'error' ? (
                <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 text-sm font-medium pr-8 leading-relaxed">{modalError.text}</div>
              <button
                onClick={() => setModalError({ type: '', text: '' })}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0 focus:outline-none transition-colors"
              >
                <span className="text-xl font-bold leading-none">&times;</span>
              </button>
            </div>
          )}

          {/* Progress Steps */}
          <div className="flex justify-between mb-8">
            {[
              { num: 1, label: 'Select Plan' },
              { num: 2, label: 'Payment' },
              { num: 3, label: 'Upload CV' },
            ].map((step) => (
              <div key={step.num} className={`flex items-center ${step.num < 3 ? 'flex-1' : ''}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    bookingStep >= step.num ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {bookingStep > step.num ? <CheckCircle className="w-5 h-5" /> : step.num}
                  </div>
                  <span className={`text-xs mt-1 ${bookingStep >= step.num ? 'text-indigo-600 font-medium' : 'text-slate-400'}`}>
                    {step.label}
                  </span>
                </div>
                {step.num < 3 && (
                  <div className={`flex-1 h-1 mx-2 mt-[-12px] ${bookingStep > step.num ? 'bg-indigo-600' : 'bg-slate-100'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Select Plan */}
          {bookingStep === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h4 className="text-lg font-semibold mb-4">Select Your Package</h4>
              <div className="space-y-3">
                {PRICING_PLANS.map((plan) => (
                  <label 
                    key={plan.id}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      activeTab === plan.id ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input 
                        type="radio" 
                        name="plan" 
                        checked={activeTab === plan.id}
                        onChange={() => setActiveTab(plan.id)}
                        className="w-5 h-5 text-indigo-600"
                      />
                      <div>
                        <div className="font-semibold text-slate-900">
                          {plan.name}
                          {plan.popular && (
                            <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
                              POPULAR
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500">{plan.appsPerWeek} apps/week • {plan.paymentNote}</div>
                      </div>
                    </div>
                    <div className="font-bold text-indigo-600">{convertPrice(plan.price, currency)}</div>
                  </label>
                ))}
              </div>

              {selectedPlan.id === 'premium' && (
                <div className="mt-4 p-4 rounded-xl border border-amber-200 bg-amber-50/50">
                  <h5 className="text-sm font-semibold text-amber-900 mb-3">Payment Option</h5>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="radio" 
                        name="paymentType" 
                        checked={paymentType === 'full'}
                        onChange={() => setPaymentType('full')}
                        className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm font-medium text-slate-800">
                        Full Payment ({convertPrice(selectedPlan.price, currency)})
                      </span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="radio" 
                        name="paymentType" 
                        checked={paymentType === 'installment'}
                        onChange={() => setPaymentType('installment')}
                        className="w-4 h-4 text-amber-600 focus:ring-amber-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-slate-800">
                          70% Installment ({convertPrice(selectedPlan.price * 0.7, currency)} now)
                        </span>
                        <p className="text-xs text-slate-500 mt-0.5">Pay remaining {convertPrice(selectedPlan.price * 0.3, currency)} within 14 days.</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              <div className="mt-6 space-y-4">
                <h4 className="text-lg font-semibold">Your Information</h4>
                <div>
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={bookingData.name}
                    onChange={(e) => {
                      setBookingData({...bookingData, name: e.target.value});
                      if (formErrors.name) setFormErrors({...formErrors, name: ''});
                    }}
                    className={`w-full p-3 border rounded-xl focus:outline-none focus:border-indigo-600 transition-colors ${
                      formErrors.name ? 'border-red-400 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />{formErrors.name}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={bookingData.email}
                    onChange={(e) => {
                      setBookingData({...bookingData, email: e.target.value});
                      if (formErrors.email) setFormErrors({...formErrors, email: ''});
                    }}
                    className={`w-full p-3 border rounded-xl focus:outline-none focus:border-indigo-600 transition-colors ${
                      formErrors.email ? 'border-red-400 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />{formErrors.email}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Phone Number (e.g. +234...)*"
                    value={bookingData.phone}
                    onChange={(e) => {
                      setBookingData({...bookingData, phone: e.target.value});
                      if (formErrors.phone) setFormErrors({...formErrors, phone: ''});
                    }}
                    className={`w-full p-3 border rounded-xl focus:outline-none focus:border-indigo-600 transition-colors ${
                      formErrors.phone ? 'border-red-400 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {formErrors.phone && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />{formErrors.phone}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked) setTermsError(false);
                    }}
                    className="mt-1 w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-slate-600">
                    I agree to the <span className="text-indigo-600 font-medium">Terms of Service</span>,{' '}
                    <span className="text-indigo-600 font-medium">Privacy Policy</span>, and{' '}
                    <span className="text-indigo-600 font-medium">Refund Policy</span>.{' '}
                    I understand that Placement Point does not guarantee job placement and that
                    refunds are only available within 24 hours of payment before any work begins.
                  </span>
                </label>
                {termsError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    You must accept the terms to continue
                  </p>
                )}
              </div>

              <button 
                onClick={handleContinueToPayment}
                className="w-full mt-6 bg-indigo-600 text-white py-4 rounded-xl font-bold hover:bg-indigo-700 transition-colors"
              >
                Continue to Payment
              </button>
            </motion.div>
          )}

          {/* Step 2: Payment */}
          {bookingStep === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <h4 className="text-lg font-semibold mb-4">Payment Method</h4>
              <div className="space-y-3 mb-6">
                <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  paymentMethod === 'card' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200'
                }`}>
                  <CreditCard className="w-6 h-6 text-indigo-600" />
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">Card Payment (Flutterwave)</div>
                    <div className="text-sm text-slate-500">Instant confirmation • Debit/Credit cards</div>
                  </div>
                  <input 
                    type="radio" 
                    name="payment" 
                    checked={paymentMethod === 'card'}
                    onChange={() => setPaymentMethod('card')}
                    className="w-5 h-5 text-indigo-600" 
                  />
                </label>

                <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  paymentMethod === 'transfer' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200'
                }`}>
                  <Banknote className="w-6 h-6 text-green-600" />
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">Bank Transfer</div>
                    <div className="text-sm text-slate-500">Upload receipt for manual verification</div>
                  </div>
                  <input 
                    type="radio" 
                    name="payment" 
                    checked={paymentMethod === 'transfer'}
                    onChange={() => setPaymentMethod('transfer')}
                    className="w-5 h-5 text-indigo-600" 
                  />
                </label>
              </div>

              {/* Bank Transfer Details */}
              {paymentMethod === 'transfer' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mb-6"
                >
                  <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                    <h5 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                      <Banknote className="w-5 h-5" />
                      Bank Transfer Details
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center py-2 border-b border-green-200/50">
                        <span className="text-green-700">Bank Name</span>
                        <span className="font-bold text-green-900">{BANK_DETAILS.bankName}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-green-200/50">
                        <span className="text-green-700">Account Name</span>
                        <span className="font-bold text-green-900">{BANK_DETAILS.accountName}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-green-700">Account Number</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-green-900 text-lg tracking-wider">{BANK_DETAILS.accountNumber}</span>
                          <button
                            onClick={() => copyToClipboard(BANK_DETAILS.accountNumber)}
                            className="p-1.5 bg-green-200/50 rounded-lg hover:bg-green-200 transition-colors"
                            title="Copy account number"
                          >
                            <Copy className="w-4 h-4 text-green-700" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {copied && (
                      <p className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" /> Account number copied!
                      </p>
                    )}
                    <p className="mt-3 text-xs text-green-600">
                      After transferring, upload your receipt below. We will verify your payment before unlocking your booking calendar.
                    </p>
                  </div>

                  {/* Receipt Upload */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Upload Payment Receipt *
                    </label>
                    <div
                      className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer bg-slate-50"
                      onClick={() => document.getElementById('receipt-upload').click()}
                    >
                      <input
                        type="file"
                        id="receipt-upload"
                        className="hidden"
                        accept="image/*,.pdf"
                        onChange={handleReceiptUpload}
                      />
                      {uploadedReceipt ? (
                        <div>
                          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                          <p className="font-semibold text-slate-700 text-sm">{uploadedReceipt.name}</p>
                          <p className="text-xs text-slate-500">Click to change file</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="font-semibold text-slate-700 text-sm">Click to upload receipt</p>
                          <p className="text-xs text-slate-500">Image (JPG, PNG) or PDF</p>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Order Summary */}
              <div className="bg-slate-50 p-4 rounded-xl mb-6">
                <h5 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wide">Order Summary</h5>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Package</span>
                  <span className="font-semibold capitalize">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Applications/week</span>
                  <span className="font-semibold">{selectedPlan.appsPerWeek}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Payment type</span>
                  <span className="font-semibold">{selectedPlan.paymentNote}</span>
                </div>
                <div className="border-t border-slate-200 my-3" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Due Today</span>
                  <span className="text-indigo-600">{convertPrice(actualPriceToPay, currency)}</span>
                </div>
                {paymentType === 'installment' && selectedPlan.id === 'premium' && (
                  <div className="mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    💡 You are paying the 70% installment. The remaining {convertPrice(selectedPlan.price * 0.3, currency)} is due within 14 days.
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setBookingStep(1)}
                  className="flex-1 py-4 rounded-xl font-bold border-2 border-slate-200 text-slate-600 hover:border-slate-300 transition-colors"
                >
                  Back
                </button>
                <button 
                  onClick={handlePayment}
                  disabled={isLoading}
                  className="flex-1 bg-indigo-600 text-white py-4 rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : paymentMethod === 'card' ? 'Pay Now' : 'Submit & Verify'}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Success & CV Upload */}
          {bookingStep === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="text-center py-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle className="w-10 h-10 text-green-600" />
              </motion.div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">
                {paymentMethod === 'transfer' ? 'Receipt Submitted!' : 'Payment Successful!'}
              </h4>
              <p className="text-slate-600 mb-2">
                {paymentMethod === 'transfer'
                  ? 'We\'ll verify your payment within 24 hours. In the meantime, upload your CV below.'
                  : 'Upload your CV and we\'ll get started right away.'}
              </p>
              <p className="text-sm text-indigo-600 font-medium mb-8">
                🗓 We respond to all bookings within 24 hours • Sessions are 45–60 min via Google Meet
              </p>

              <div 
                className="border-2 border-dashed border-slate-300 rounded-2xl p-8 mb-6 hover:border-indigo-400 transition-colors cursor-pointer bg-slate-50 relative"
                onClick={() => document.getElementById('cv-upload').click()}
              >
                <input type="file" id="cv-upload" className="hidden" accept=".pdf,.doc,.docx" onChange={handleFileUpload} />
                {uploadedFile ? (
                  <div className="text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="font-semibold text-slate-700">{uploadedFile.name}</p>
                    <p className="text-sm text-slate-500">Click to change file</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="font-semibold text-slate-700">Click to upload your current CV</p>
                    <p className="text-sm text-slate-500">PDF or Word (Max 5MB)</p>
                  </>
                )}
              </div>

              <button 
                onClick={handleCompleteBooking}
                disabled={isLoading}
                className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Completing...</> : 'Complete Booking'}
              </button>

              <p className="mt-4 text-xs text-slate-400">
                By completing this booking, you agree that the final hiring decision rests with the employer.
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BookingModal;
